import React, { useEffect, useState } from "react";
import {
  FaTrash,
  FaFilter,
  FaTimes,
  FaPlus,
  FaWallet,
  FaCheckCircle,
  FaTimesCircle,
  FaEdit,
  FaSave,
} from "react-icons/fa";
import {
  upiList,
  addUpi,
  updateUpi,
  updateUpiStatus,
  deleteUpi,
} from "../../api/endpoints";
import Swal from "../../utils/swalCompat";
import PageHeader from "../../components/PageHeader";

// ============================================================
// ✅ ADD / EDIT UPI MODAL
// ============================================================
const UpiModal = ({ show, setShow, editData, onSuccess }) => {
  const [formData, setFormData] = useState({
    upiId: "",
    holderName: "",
  });
  const [saving, setSaving] = useState(false);

  // Reset / Prefill
  useEffect(() => {
    if (show) {
      if (editData) {
        setFormData({
          upiId: editData.upiId || "",
          holderName: editData.holderName || "",
        });
      } else {
        setFormData({ upiId: "", holderName: "" });
      }
    }
  }, [show, editData]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validations
    if (!formData.upiId.trim()) {
      Swal.fire("Error", "Please enter UPI ID", "error");
      return;
    }
    if (!formData.holderName.trim()) {
      Swal.fire("Error", "Please enter holder name", "error");
      return;
    }

    const upiRegex = /^[a-zA-Z0-9.\-_]{2,}@[a-zA-Z]{2,}$/;
    if (!upiRegex.test(formData.upiId.trim())) {
      Swal.fire(
        "Invalid UPI ID",
        "Please enter valid UPI ID (e.g. example@ybl)",
        "error",
      );
      return;
    }

    try {
      setSaving(true);

      let res;
      if (editData) {
        // ✅ UPDATE API
        res = await updateUpi(editData._id, {
          upiId: formData.upiId.trim(),
          holderName: formData.holderName.trim(),
        });
      } else {
        // ✅ ADD API
        res = await addUpi({
          upiId: formData.upiId.trim(),
          holderName: formData.holderName.trim(),
        });
      }

      console.log("API Response:", res);

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: editData ? "Updated!" : "Added!",
          text: `UPI ${editData ? "updated" : "added"} successfully`,
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        setShow(false);
        if (onSuccess) onSuccess();
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: res.message || "Operation failed",
        });
      }
    } catch (error) {
      console.error("UPI save error:", error);
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text:
          error.response?.data?.message ||
          "Failed to save UPI. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <div
      className="modal fade show d-block"
      style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
      tabIndex="-1"
      onClick={() => setShow(false)}
    >
      <div
        className="modal-dialog modal-dialog-centered"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content border-0" style={{ borderRadius: 14 }}>
          {/* Header */}
          <div
            className="modal-header border-0 text-white"
            style={{
              background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
              padding: "16px 20px",
            }}
          >
            <div className="d-flex align-items-center gap-2">
              <div
                className="d-flex align-items-center justify-content-center rounded-3"
                style={{
                  width: 36,
                  height: 36,
                  background: "rgba(255,255,255,0.2)",
                }}
              >
                <FaWallet size={15} />
              </div>
              <div>
                <h5 className="modal-title fw-bold mb-0">
                  {editData ? "Edit UPI ID" : "Add UPI ID"}
                </h5>
                <small style={{ opacity: 0.85, fontSize: "0.72rem" }}>
                  {editData
                    ? "Update UPI details"
                    : "Add a new UPI ID for payments"}
                </small>
              </div>
            </div>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={() => setShow(false)}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body p-4">
            {/* UPI ID */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                UPI ID <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="upiId"
                className="form-control form-control-lg"
                placeholder="e.g. example@ybl"
                value={formData.upiId}
                onChange={handleChange}
                disabled={saving}
                autoFocus
              />
              <small className="text-muted d-block mt-1">
                Virtual Payment Address — e.g. yourname@ybl, yourname@paytm
              </small>
            </div>

            {/* Holder Name */}
            <div className="mb-3">
              <label className="form-label fw-semibold">
                Holder Name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="holderName"
                className="form-control form-control-lg"
                placeholder="e.g. Ram Rummy Official"
                value={formData.holderName}
                onChange={handleChange}
                disabled={saving}
              />
            </div>

            {/* Info note */}
            {!editData && (
              <div
                className="p-3 rounded-3 small"
                style={{ background: "#FEF3C7", color: "#B45309" }}
              >
                <strong>ℹ️ Note:</strong> New UPI will be added as{" "}
                <strong>INACTIVE</strong>. You can activate it from the list.
              </div>
            )}
          </div>

          {/* Footer */}
          <div
            className="modal-footer border-0"
            style={{ padding: "12px 20px 18px" }}
          >
            <button
              type="button"
              className="btn btn-light border"
              onClick={() => setShow(false)}
              disabled={saving}
              style={{ fontWeight: 600 }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleSubmit}
              disabled={saving}
              style={{
                background: "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                border: "none",
                fontWeight: 600,
                paddingLeft: 20,
                paddingRight: 20,
              }}
            >
              {saving ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                  ></span>
                  Saving...
                </>
              ) : (
                <>
                  {editData ? <FaSave size={12} /> : <FaPlus size={12} />}
                  {editData ? "Update" : "Add UPI"}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// MAIN COMPONENT
// ============================================================
const UpiSetting = () => {
  const [upiData, setUpiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Filters
  const [showFilter, setShowFilter] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState(null);

  // ============================================================
  // FETCH UPI LIST
  // ============================================================
  const fetchUpiList = async (pageNum = page) => {
    try {
      setLoading(true);
      const params = { page: pageNum, limit };

      if (search.trim()) params.search = search.trim();
      if (statusFilter) params.status = statusFilter;

      console.log("Fetching UPI list with params:", params);
      const res = await upiList(params);
      console.log("UPI List Response:", res);

      if (res.success) {
        const { upis = [], pagination = {} } = res.data || {};
        setUpiData(upis);
        setTotalPages(pagination.totalPages || 1);
        setTotalItems(pagination.total || 0);
        setPage(pagination.page || 1);
      } else {
        Swal.fire("Error", res.message || "Failed to fetch UPI list", "error");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Failed to fetch UPI list",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUpiList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Debounced search + status filter
  useEffect(() => {
    const timer = setTimeout(() => {
      if (search || statusFilter) {
        setPage(1);
        fetchUpiList(1);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter]);

  // ============================================================
  // LOCAL FILTER (extra safety)
  // ============================================================
  const filteredList = upiData.filter((item) => {
    const q = search.trim().toLowerCase();
    const matchesSearch =
      !q ||
      item.upiId?.toLowerCase().includes(q) ||
      item.holderName?.toLowerCase().includes(q);

    const matchesStatus = !statusFilter || item.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const hasActiveFilters = search || statusFilter;

  const clearFilters = () => {
    setSearch("");
    setStatusFilter("");
    setPage(1);
  };

  // ============================================================
  // ADD / EDIT
  // ============================================================
  const handleOpenAdd = () => {
    setEditData(null);
    setShowModal(true);
  };

  const handleOpenEdit = (item) => {
    setEditData(item);
    setShowModal(true);
  };

  const handleModalSuccess = () => {
    // Refresh from API
    fetchUpiList(page);
  };

  // ============================================================
  // DELETE — API
  // ============================================================
  const handleDelete = async (id, upiId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: `Delete "${upiId}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Delete",
    });

    if (!result.isConfirmed) return;

    try {
      setDeletingId(id);
      const res = await deleteUpi(id);
      console.log("Delete response:", res);

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: res.message || "UPI deleted successfully",
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });
        fetchUpiList(page);
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed!",
          text: res.message || "Failed to delete UPI",
        });
      }
    } catch (error) {
      console.error("Delete error:", error);
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: error.response?.data?.message || "Failed to delete UPI",
      });
    } finally {
      setDeletingId(null);
    }
  };

  // ============================================================
  // STATUS TOGGLE — API
  // ============================================================
  const handleStatusToggle = async (id, currentStatus) => {
    const newStatus = currentStatus === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const action = newStatus === "ACTIVE" ? "activate" : "deactivate";

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You want to ${action} this UPI?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: newStatus === "ACTIVE" ? "#28a745" : "#d33",
      confirmButtonText: `Yes, ${action}`,
    });

    if (!result.isConfirmed) return;

    try {
      setTogglingId(id);
      const res = await updateUpiStatus(id, newStatus);
      console.log("Status update response:", res);

      if (res.success) {
        // Update local state immediately
        setUpiData((prev) =>
          prev.map((item) =>
            item._id === id ? { ...item, status: newStatus } : item,
          ),
        );

        Swal.fire({
          icon: "success",
          title: "Success",
          text: `UPI ${action}d successfully`,
          timer: 1500,
          showConfirmButton: false,
          toast: true,
          position: "top-end",
        });

        // Optional: refresh to ensure sync
        fetchUpiList(page);
      } else {
        Swal.fire({
          icon: "error",
          title: "Cannot Change Status",
          text: res.message || `Failed to ${action} UPI`,
        });
      }
    } catch (error) {
      console.error("Status update error:", error);
      Swal.fire({
        icon: "error",
        title: "Cannot Change Status",
        text:
          error.response?.data?.message ||
          `Failed to ${action} UPI. Please try again.`,
      });
    } finally {
      setTogglingId(null);
    }
  };

  // ============================================================
  // PAGINATION
  // ============================================================
  const goToPage = (pageNum) => {
    if (pageNum >= 1 && pageNum <= totalPages) setPage(pageNum);
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ============================================================
  // FORMAT DATE
  // ============================================================
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="lead-page">
      {/* Header */}
      <PageHeader
        title="UPI Management"
        subtitle="Manage all UPI IDs"
        actions={
          <>
            <button
              className="btn btn-primary d-flex align-items-center gap-2"
              onClick={handleOpenAdd}
            >
              <FaPlus size={12} /> Add UPI
            </button>
          </>
        }
      />

      {/* Main Card */}
      <div className="card mb-4 card-light">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
          <h5 className="card-title mb-0">UPI List</h5>
          <div className="d-flex gap-2 align-items-center flex-wrap">
            {showFilter && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search UPI ID or name..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: "220px" }}
                />

                <select
                  className="form-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ width: "140px" }}
                >
                  <option value="">All Status</option>
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>

                {hasActiveFilters && (
                  <button
                    className="btn btn-outline-danger"
                    onClick={clearFilters}
                    title="Clear all filters"
                  >
                    <FaTimes /> Clear
                  </button>
                )}
              </div>
            )}
            <button
              className={`btn ${showFilter ? "btn-primary" : "btn-dark"}`}
              onClick={() => setShowFilter(!showFilter)}
            >
              <FaFilter />
              {showFilter ? " Hide" : " Filter"}
            </button>
          </div>
        </div>

        <div className="card-body">
          {/* Info row */}
          <div className="mb-3 d-flex justify-content-between align-items-center flex-wrap">
            <div>
              <span className="text-muted">
                Showing {filteredList.length} of {totalItems} UPI IDs
              </span>
              {hasActiveFilters && (
                <span className="badge bg-info ms-2">Filters Applied</span>
              )}
            </div>
            <div>
              <span className="text-muted">
                Page {page} of {totalPages}
              </span>
            </div>
          </div>

          {/* Active filter badges */}
          {hasActiveFilters && (
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {search && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Search: {search}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setSearch("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
              {statusFilter && (
                <span className="badge bg-secondary d-flex align-items-center gap-1">
                  Status: {statusFilter}
                  <button
                    className="btn btn-sm p-0 text-white"
                    onClick={() => setStatusFilter("")}
                    style={{ background: "none", border: "none" }}
                  >
                    <FaTimes />
                  </button>
                </span>
              )}
            </div>
          )}

          {/* Table */}
          <div className="table-responsive">
            <table className="table align-middle">
              <thead>
                <tr>
                  <th>#</th>
                  <th>UPI ID</th>
                  <th>Holder Name</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && upiData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : filteredList.length > 0 ? (
                  filteredList.map((item, index) => (
                    <tr key={item._id}>
                      <td>{(page - 1) * limit + index + 1}</td>

                      {/* UPI ID */}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="rounded-circle text-white d-flex align-items-center justify-content-center"
                            style={{
                              width: "35px",
                              height: "35px",
                              background:
                                "linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)",
                            }}
                          >
                            <FaWallet size={14} />
                          </div>
                          <span className="fw-semibold">{item.upiId}</span>
                        </div>
                      </td>

                      {/* Holder Name */}
                      <td>{item.holderName}</td>

                      {/* Status Toggle + Badge */}
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <label
                            style={{
                              position: "relative",
                              display: "inline-block",
                              width: "50px",
                              height: "24px",
                              marginBottom: 0,
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={item.status === "ACTIVE"}
                              onChange={() =>
                                handleStatusToggle(item._id, item.status)
                              }
                              disabled={togglingId === item._id}
                              style={{
                                opacity: 0,
                                width: 0,
                                height: 0,
                              }}
                            />
                            <span
                              style={{
                                position: "absolute",
                                cursor: "pointer",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                backgroundColor:
                                  item.status === "ACTIVE" ? "#4CAF50" : "#ccc",
                                transition: ".4s",
                                borderRadius: "34px",
                                opacity: togglingId === item._id ? 0.5 : 1,
                              }}
                            >
                              <span
                                style={{
                                  position: "absolute",
                                  height: "16px",
                                  width: "16px",
                                  left:
                                    item.status === "ACTIVE" ? "26px" : "4px",
                                  bottom: "4px",
                                  backgroundColor: "white",
                                  transition: ".4s",
                                  borderRadius: "50%",
                                }}
                              />
                            </span>
                          </label>
                          <span
                            className={`badge ${
                              item.status === "ACTIVE"
                                ? "bg-success"
                                : "bg-danger"
                            }`}
                            style={{ minWidth: "70px" }}
                          >
                            {item.status}
                          </span>
                          {togglingId === item._id && (
                            <span
                              className="spinner-border spinner-border-sm text-primary"
                              role="status"
                            ></span>
                          )}
                        </div>
                      </td>

                      {/* Created */}
                      <td>{formatDate(item.createdAt)}</td>

                      {/* Actions */}
                      <td>
                        <div className="d-flex gap-2 justify-content-center">
                          <button
                            className="btn btn-sm btn-info custum_edit text-white"
                            onClick={() => handleOpenEdit(item)}
                            title="Edit UPI"
                          >
                            <FaEdit />
                          </button>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDelete(item._id, item.upiId)}
                            disabled={deletingId === item._id}
                            title="Delete UPI"
                          >
                            {deletingId === item._id ? (
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                              ></span>
                            ) : (
                              <FaTrash />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-4">
                      {hasActiveFilters
                        ? "No UPI IDs found matching your filters"
                        : "No UPI IDs Found — Add your first UPI above"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1 || loading}
              >
                Previous
              </button>

              <div className="d-flex gap-1">
                {getPageNumbers().map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`btn ${
                      pageNum === page ? "btn-primary" : "btn-outline-primary"
                    }`}
                    onClick={() => goToPage(pageNum)}
                    disabled={loading}
                    style={{ minWidth: "40px" }}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>

              <button
                className="btn btn-outline-secondary"
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages || loading}
              >
                Next
              </button>
            </div>
          )}

          <div className="d-flex justify-content-center mt-2">
            <small className="text-muted">
              Showing {filteredList.length} of {totalItems} UPI IDs | Page{" "}
              {page} of {totalPages}
            </small>
          </div>
        </div>
      </div>

      {/* Modal */}
      <UpiModal
        show={showModal}
        setShow={setShowModal}
        editData={editData}
        onSuccess={handleModalSuccess}
      />
    </div>
  );
};

export default UpiSetting;
